import { ExperimentMetric } from 'src/services';

export interface TierAMetricConfig {
  metric: ExperimentMetric;
  label: string;
  description: string;
}

// Average Order Value is deliberately excluded here: the wizard's duration estimator
// (backend-projects/platform-be ExperimentsWizardService.estimateDuration) always rejects it as a
// deciding metric — a variant can change who orders without revenue moving — so offering it would
// let someone pick and launch an experiment that can never produce a valid estimate.
export const TIER_A_METRICS: TierAMetricConfig[] = [
  {
    metric: ExperimentMetric.UserConversion,
    label: 'Conversion Rate',
    description: 'The share of visitors who complete a purchase.',
  },
  {
    metric: ExperimentMetric.AverageRevenuePerUser,
    label: 'Average Revenue Per User',
    description: 'The average revenue generated per visitor.',
  },
];

export interface TierBMetricConfig {
  key: string;
  label: string;
  description: string;
}

export const TIER_B_METRICS: TierBMetricConfig[] = [
  {
    key: 'click-through-rate',
    label: 'Click-Through Rate',
    description: 'The share of visitors who click on a recommendation.',
  },
  {
    key: 'add-to-cart-rate',
    label: 'Add-to-Cart Rate',
    description: 'The share of visitors who add a recommended item to their cart.',
  },
  {
    key: 'revenue-per-visit',
    label: 'Revenue Per Visit',
    description: 'The average revenue generated per site visit.',
  },
];

const SURFACE_DEFAULT = 'All surfaces';
const LEVER_SUMMARY_DEFAULT = 'Baseline: no merchandising rules';

export function buildDefaultHypothesis(metricLabel: string): string {
  return `If we turn every rule off for a small slice to measure what your rules are actually worth, ${metricLabel} will go up for shoppers who see ${SURFACE_DEFAULT} — and if it does not, we stop doing it.`;
}

const NAME_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

export function buildDefaultName(
  surfaceLabel: string = SURFACE_DEFAULT,
  leverSummary: string = LEVER_SUMMARY_DEFAULT
): string {
  const date = new Intl.DateTimeFormat('en-US', NAME_DATE_FORMAT).format(new Date());

  return `${surfaceLabel} · ${leverSummary} · ${date}`;
}

const AUTO_NAME_PATTERN = /^.+ · .+ · [A-Za-z]{3} \d{1,2}, \d{4}, \d{1,2}:\d{2}:\d{2}\s?[AP]M$/;

export function isAutoGeneratedName(name: string): boolean {
  return AUTO_NAME_PATTERN.test(name);
}

export function formatBaselineValue(metric: ExperimentMetric, value: number): string {
  if (metric === ExperimentMetric.UserConversion) {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

export function buildSensitivityCopy(
  metricLabel: string,
  dailyTestUsers: number,
  minEffectPercent: number
): string {
  return `Sensitivity: at ${dailyTestUsers.toLocaleString()} test users a day, this test can only prove a change of ${minEffectPercent}% or larger in ${metricLabel}. Anything smaller comes back as "no difference" — that is a real result, not a failed test. Set the number you would act on in the traffic step.`;
}
